import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import { feedbackService } from "@/services";
import { ClientFeedback } from "@shared/types/FeedbackDto";

interface ClientFeedbackContextType {
  feedback: ClientFeedback[];
  isLoading: boolean;
  error: string | null;
  fetchFeedback: () => Promise<void>;
}

const ClientFeedbackContext = createContext<ClientFeedbackContextType | null>(
  null,
);

export function ClientFeedbackProvider({ children }: { children: ReactNode }) {
  const [feedback, setFeedback] = useState<ClientFeedback[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFeedback = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await feedbackService.getMyFeedback();
      setFeedback(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch client feedback",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  return (
    <ClientFeedbackContext.Provider
      value={{
        feedback,
        isLoading,
        error,
        fetchFeedback,
      }}
    >
      {children}
    </ClientFeedbackContext.Provider>
  );
}

export function useClientFeedback() {
  const context = useContext(ClientFeedbackContext);
  if (!context) {
    throw new Error(
      "useClientFeedback must be used within a ClientFeedbackProvider",
    );
  }
  return context;
}
